/**
 * A list that arrives in order rather than all at once.
 *
 * Same rule as `Reveal`, for the same reason: the children move on `transform`
 * and their opacity is never touched. The delay is capped, because a stagger
 * that scales with the list length makes a twelve-row table take three seconds
 * to finish arriving, and by then the reader has scrolled past it.
 */
import { m } from 'motion/react'
import type { ReactNode } from 'react'
import { Children } from 'react'

import { useReducedMotionSafe } from './motion'

const MAX_TOTAL = 0.6

export function Stagger({
  children,
  step = 0.05,
  y = 10,
  className,
  as = 'div',
  childAs = 'div',
}: {
  children: ReactNode
  step?: number
  y?: number
  className?: string
  as?: 'div' | 'ul' | 'ol' | 'tbody'
  childAs?: 'div' | 'li' | 'tr'
}) {
  const reduced = useReducedMotionSafe()
  const items = Children.toArray(children)
  const Wrapper = as

  /*
   * Both branches build the same tree; only the timeline differs.
   *
   * The reduced branch used to render `children` straight into the wrapper,
   * which meant a `<ul>` full of `<div>`s — invalid markup that axe reports as a
   * serious `list` violation, and that a screen reader announces as no list at
   * all. It was invisible in review because the animated branch, the one you
   * look at, was correct. Two branches producing different DOM is the defect;
   * the accessibility failure was only how it surfaced.
   */
  const Plain = childAs
  if (reduced) {
    return (
      <Wrapper className={className}>
        {items.map((child, i) => (
          <Plain key={i}>{child}</Plain>
        ))}
      </Wrapper>
    )
  }

  const each = items.length > 1 ? Math.min(step, MAX_TOTAL / (items.length - 1)) : 0
  const Child = m[childAs]

  return (
    <Wrapper className={className}>
      {items.map((child, i) => (
        <Child
          key={i}
          initial={{ y }}
          whileInView={{ y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.3, delay: i * each, ease: [0.32, 0.72, 0, 1] }}
        >
          {child}
        </Child>
      ))}
    </Wrapper>
  )
}
